import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import Swal from 'sweetalert2';
import AdminSalesLayout from '../../components/AdminSalesLayout';
import YearPicker from '../../components/YearPicker';
import axiosInstance from '../../utils/axiosInstance';
import {
  listBranchMonthlyTargets,
  bulkSaveBranchMonthlyTargets,
  getMonthlyTargetKpi,
  saveMonthlyTargetKpi,
} from '../../services/branchMonthlyTargetService';

// ─── Financial year months (Apr → Mar) ────────────────────────────────────────
// monthIndex = 0-based index within the financial year
// calMonth   = 1-based calendar month
const FY_MONTHS = [
  { label: 'April',     calMonth: 4,  offset: 0 },
  { label: 'May',       calMonth: 5,  offset: 0 },
  { label: 'June',      calMonth: 6,  offset: 0 },
  { label: 'July',      calMonth: 7,  offset: 0 },
  { label: 'August',    calMonth: 8,  offset: 0 },
  { label: 'September', calMonth: 9,  offset: 0 },
  { label: 'October',   calMonth: 10, offset: 0 },
  { label: 'November',  calMonth: 11, offset: 0 },
  { label: 'December',  calMonth: 12, offset: 0 },
  { label: 'January',   calMonth: 1,  offset: 1 },
  { label: 'February',  calMonth: 2,  offset: 1 },
  { label: 'March',     calMonth: 3,  offset: 1 },
];

const CURRENT_YEAR = new Date().getFullYear();

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toDisplayValue(raw) {
  if (raw === null || raw === undefined || raw === '') return '';
  const n = parseFloat(raw);
  if (isNaN(n)) return '';
  return n % 1 === 0
    ? n.toLocaleString('en-US')
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseVal(v) {
  return parseFloat(String(v).replace(/,/g, '')) || 0;
}

function formatVal(v) {
  const n = parseFloat(String(v).replace(/,/g, ''));
  if (!n && n !== 0) return '';
  return n % 1 === 0
    ? n.toLocaleString('en-US')
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Build a unique key for each month in the grid
function mKey(fyYear, calMonth) {
  return `${fyYear}_${calMonth}`;
}

// ─── Styled components ────────────────────────────────────────────────────────
const PageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 88px;
`;

const YearPickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 24px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
`;

const YearPickerLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const YearPickerWrap = styled.div`
  width: 180px;
`;

const FYBadge = styled.span`
  margin-left: 4px;
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
`;

const StatCard = styled.div`
  background: ${(p) => p.$bg || '#ffffff'};
  border: 1px solid ${(p) => p.$border || '#e2e8f0'};
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #64748b;
`;

const StatValue = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: ${(p) => p.$color || '#0f172a'};
  line-height: 1.2;
`;

const TableCard = styled.section`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const CardSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 480px;
`;

const Th = styled.th`
  text-align: ${(p) => (p.$right ? 'right' : 'left')};
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 16px;
  border-bottom: 2px solid #e2e8f0;
  background: #f8fafc;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #0f172a;
  vertical-align: middle;
  background: #ffffff;
`;

const Tr = styled.tr`
  transition: background 0.12s;
  &:hover td {
    background: #f0fdf4 !important;
  }
  &:last-child td {
    border-bottom: none;
  }
`;

const QuarterTr = styled(Tr)`
  td {
    background: #f8fafc !important;
    font-weight: 700;
    font-size: 13px;
    color: #334155;
    border-top: 2px solid #e2e8f0;
  }
`;

const MonthChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`;

const YearChip = styled.span`
  font-size: 11px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 2px 7px;
  color: #64748b;
  font-weight: 600;
`;

const CellInput = styled.input`
  border: 2px solid ${(p) => (p.$dirty ? '#f59e0b' : '#e2e8f0')};
  background: ${(p) => (p.$dirty ? '#fffbeb' : '#f8fafc')};
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 15px;
  font-weight: ${(p) => (p.$dirty ? '700' : '500')};
  color: #0f172a;
  text-align: right;
  width: 220px;
  box-sizing: border-box;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  &:focus {
    border-color: #10b981;
    background: #f0fdf4;
    outline: none;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
  }
  &::placeholder {
    color: #cbd5e1;
  }
`;

const QuarterTotal = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1e40af;
`;

const ErrorBanner = styled.div`
  border: 1px solid #fecdd3;
  background: #fff1f2;
  color: #b91c1c;
  border-radius: 10px;
  padding: 14px 18px;
  font-size: 14px;
  margin-bottom: 16px;
`;

// Sticky save bar
const SaveBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  background: #0f172a;
  padding: 14px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  box-shadow: 0 -4px 24px rgba(15, 23, 42, 0.25);
  transform: translateY(${(p) => (p.$visible ? '0' : '100%')});
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
`;

const SaveBarLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SaveBarTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #f1f5f9;
`;

const SaveBarSub = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const SaveBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DiscardBtn = styled.button`
  border: 1px solid #334155;
  background: transparent;
  color: #94a3b8;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  &:hover {
    color: #e2e8f0;
    border-color: #64748b;
  }
`;

const SaveBtn = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 11px 26px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.45);
  transition: transform 0.15s, box-shadow 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(16, 185, 129, 0.55);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingRow = styled.tr`
  td {
    text-align: center;
    padding: 48px 0;
    color: #94a3b8;
    font-size: 15px;
  }
`;

const KpiCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) => (p.$set ? '#f0fdf4' : '#f8fafc')};
  color: ${(p) => (p.$set ? '#15803d' : '#94a3b8')};
  border: 1px solid ${(p) => (p.$set ? '#bbf7d0' : '#e2e8f0')};
  border-radius: 20px;
  padding: 2px 9px;
  white-space: nowrap;
`;

const KpiBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  background: transparent;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  border-radius: 7px;
  padding: 5px 11px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  &:hover:not(:disabled) {
    background: #eff6ff;
    border-color: #93c5fd;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ─── KPI Modal styled components ─────────────────────────────────────────────
const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ModalBox = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.22);
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 3px 0 0;
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  font-size: 20px;
  line-height: 1;
  padding: 0 2px;
  &:hover { color: #0f172a; }
`;

const ModalBody = styled.div`
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ModalFooter = styled.div`
  padding: 14px 24px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const AddRowBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  color: #2563eb;
  background: #eff6ff;
  border: 1px dashed #93c5fd;
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  transition: background 0.12s;
  &:hover { background: #dbeafe; }
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 140px 140px 36px;
  gap: 8px;
  align-items: center;
`;

const KpiRowHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 140px 140px 36px;
  gap: 8px;
  padding: 0 2px 4px;
  border-bottom: 1px solid #f1f5f9;
`;

const KpiHeaderCell = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const KpiSelect = styled.div`
  width: 100%;
`;

const KpiInput = styled.input`
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  padding: 8px 10px;
  font-size: 13px;
  color: #0f172a;
  text-align: right;
  background: #f8fafc;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #10b981; background: #fff; }
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #ef4444;
  font-size: 18px;
  line-height: 1;
  padding: 0;
  &:hover { color: #b91c1c; }
`;

const ModalSaveBtn = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 22px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ModalCancelBtn = styled.button`
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  &:hover { border-color: #94a3b8; color: #0f172a; }
`;

const ModalError = styled.div`
  font-size: 13px;
  color: #b91c1c;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 8px;
  padding: 10px 14px;
`;

// Quarter label helper
const QUARTERS = [
  { label: 'Q1', indices: [0, 1, 2] },   // Apr May Jun
  { label: 'Q2', indices: [3, 4, 5] },   // Jul Aug Sep
  { label: 'Q3', indices: [6, 7, 8] },   // Oct Nov Dec
  { label: 'Q4', indices: [9, 10, 11] }, // Jan Feb Mar
];

function getQuarterForIndex(idx) {
  return QUARTERS.find((q) => q.indices[0] === idx) || null;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function BranchMonthlyTarget() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [values, setValues] = useState({});
  const [original, setOriginal] = useState({});
  const [targetIds, setTargetIds] = useState({}); // mKey -> id
  const [kpiCounts, setKpiCounts] = useState({}); // mKey -> count
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ── KPI modal state ────────────────────────────────────────────────────────
  const [kpiModal, setKpiModal] = useState(null); // { targetId, monthLabel }
  const [kpiRows, setKpiRows] = useState([]);     // [{ _uid, staff_id, kpi_point, kpi_amount }]
  const [kpiModalLoading, setKpiModalLoading] = useState(false);
  const [kpiModalSaving, setKpiModalSaving] = useState(false);
  const [kpiModalError, setKpiModalError] = useState('');
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const uidRef = useRef(0);
  function nextUid() { uidRef.current += 1; return uidRef.current; }

  // Build empty values map from FY months
  function buildEmptyValues(fyYear) {
    const map = {};
    FY_MONTHS.forEach(({ calMonth, offset }) => {
      map[mKey(fyYear + offset, calMonth)] = '';
    });
    return map;
  }

  // ── fetch ──────────────────────────────────────────────────────────────────
  async function fetchData(fyYear) {
    setLoading(true);
    setError(null);
    try {
      const data = await listBranchMonthlyTargets({ year: fyYear });
      const rows = data.data || [];
      const map = buildEmptyValues(fyYear);
      const idMap = {};
      const countMap = {};
      rows.forEach((r) => {
        const k = mKey(r.year, r.month);
        if (k in map) {
          map[k] = toDisplayValue(r.amount);
          idMap[k] = r.id;
          countMap[k] = Number(r.kpi_count) || 0;
        }
      });
      setValues({ ...map });
      setOriginal({ ...map });
      setTargetIds(idMap);
      setKpiCounts(countMap);
    } catch {
      setError('Failed to load monthly targets. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  // ── dirty tracking ─────────────────────────────────────────────────────────
  const dirtyKeys = Object.keys(values).filter((k) => values[k] !== (original[k] ?? ''));
  const isDirty = dirtyKeys.length > 0;

  // ── cell change ────────────────────────────────────────────────────────────
  function handleChange(key, val) {
    const trimmed = val.replace(/\s/g, '');
    if (trimmed !== '' && !/^[\d,]*\.?\d*$/.test(trimmed)) return;
    setValues((prev) => ({ ...prev, [key]: trimmed }));
  }

  function handleBlur(key) {
    setValues((prev) => ({ ...prev, [key]: formatVal(prev[key]) }));
  }

  // ── discard ────────────────────────────────────────────────────────────────
  function handleDiscard() {
    setValues({ ...original });
  }

  // ── save ───────────────────────────────────────────────────────────────────
  async function handleSave() {
    // Build array of all 12 months (upsert all, even zero/empty as 0)
    const targets = FY_MONTHS.map(({ calMonth, offset }) => {
      const calYear = year + offset;
      const k = mKey(calYear, calMonth);
      return {
        month: calMonth,
        year: calYear,
        amount: parseVal(values[k]),
      };
    });

    setSaving(true);
    setError(null);
    try {
      const data = await bulkSaveBranchMonthlyTargets({ fyYear: year, targets });
      const rows = data.data || [];
      const map = buildEmptyValues(year);
      const idMap = {};
      const countMap = {};
      rows.forEach((r) => {
        const k = mKey(r.year, r.month);
        if (k in map) {
          map[k] = toDisplayValue(r.amount);
          idMap[k] = r.id;
          countMap[k] = Number(r.kpi_count) || 0;
        }
      });
      setValues({ ...map });
      setOriginal({ ...map });
      setTargetIds(idMap);
      setKpiCounts(countMap);
      Swal.fire({
        icon: 'success',
        title: 'Saved!',
        text: `Monthly targets for FY ${year}/${String(year + 1).slice(-2)} saved successfully.`,
        timer: 2500,
        timerProgressBar: true,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save. Please try again.';
      setError(msg);
      Swal.fire({ icon: 'error', title: 'Save Failed', text: msg, confirmButtonColor: '#10b981' });
    } finally {
      setSaving(false);
    }
  }

  // ── KPI modal handlers ─────────────────────────────────────────────────────
  async function openKpiModal(k, monthLabel) {
    const targetId = targetIds[k];
    if (!targetId) return;
    setKpiModal({ targetId, monthLabel, k });
    setKpiModalError('');
    setKpiModalLoading(true);
    setKpiRows([]);
    try {
      const [kpiData] = await Promise.all([
        getMonthlyTargetKpi(targetId),
        staffOptions.length === 0 ? axiosInstance.get('/admin/staff').then((r) => {
          setStaffOptions(Array.isArray(r.data?.staff) ? r.data.staff : []);
        }).catch(() => {}) : Promise.resolve(),
      ]);
      setKpiRows(
        (kpiData.kpi || []).map((row) => ({
          _uid: nextUid(),
          staff_id: String(row.staff_id),
          kpi_point: String(row.kpi_point ?? ''),
          kpi_amount: row.kpi_amount !== null && row.kpi_amount !== undefined ? String(row.kpi_amount) : '',
        }))
      );
    } catch {
      setKpiModalError('Failed to load KPI data.');
    } finally {
      setKpiModalLoading(false);
    }
    // load staff if not yet loaded
    if (staffOptions.length === 0 && !staffLoading) {
      setStaffLoading(true);
      axiosInstance.get('/admin/staff')
        .then((r) => setStaffOptions(Array.isArray(r.data?.staff) ? r.data.staff : []))
        .catch(() => {})
        .finally(() => setStaffLoading(false));
    }
  }

  function closeKpiModal() { setKpiModal(null); setKpiRows([]); setKpiModalError(''); }

  function addKpiRow() {
    setKpiRows((prev) => [...prev, { _uid: nextUid(), staff_id: '', kpi_point: '', kpi_amount: '' }]);
  }

  function removeKpiRow(uid) {
    setKpiRows((prev) => prev.filter((r) => r._uid !== uid));
  }

  function updateKpiRow(uid, field, value) {
    setKpiRows((prev) => prev.map((r) => r._uid === uid ? { ...r, [field]: value } : r));
  }

  async function handleKpiSave() {
    if (!kpiModal) return;
    setKpiModalError('');
    // Basic validation
    for (const row of kpiRows) {
      if (!row.staff_id) { setKpiModalError('Please select a counselor for every row.'); return; }
      if (row.kpi_point === '' || isNaN(parseFloat(row.kpi_point))) {
        setKpiModalError('KPI Point is required and must be a number.'); return;
      }
    }
    // Check duplicates
    const ids = kpiRows.map((r) => r.staff_id);
    if (new Set(ids).size !== ids.length) { setKpiModalError('Duplicate counselors are not allowed.'); return; }

    setKpiModalSaving(true);
    try {
      await saveMonthlyTargetKpi(
        kpiModal.targetId,
        kpiRows.map((r) => ({
          staff_id: Number(r.staff_id),
          kpi_point: parseFloat(r.kpi_point),
          kpi_amount: r.kpi_amount !== '' ? parseFloat(r.kpi_amount) : null,
        }))
      );
      // Update local kpi count
      setKpiCounts((prev) => ({ ...prev, [kpiModal.k]: kpiRows.length }));
      closeKpiModal();
      Swal.fire({
        icon: 'success', title: 'KPI Saved!',
        text: `KPI targets for ${kpiModal.monthLabel} saved.`,
        timer: 2000, timerProgressBar: true, showConfirmButton: false,
        toast: true, position: 'top-end',
      });
    } catch (err) {
      setKpiModalError(err?.response?.data?.error || 'Failed to save KPI.');
    } finally {
      setKpiModalSaving(false);
    }
  }

  // ── derived totals ─────────────────────────────────────────────────────────
  const grandTotal = FY_MONTHS.reduce((sum, { calMonth, offset }) => {
    return sum + parseVal(values[mKey(year + offset, calMonth)]);
  }, 0);

  const filledCount = FY_MONTHS.filter(({ calMonth, offset }) => {
    return parseVal(values[mKey(year + offset, calMonth)]) > 0;
  }).length;

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <AdminSalesLayout title="Monthly Target" currentSection="branchMonthlyTarget">
      <PageSection>

        {/* Year selector */}
        <YearPickerRow>
          <YearPickerLabel>Financial Year:</YearPickerLabel>
          <YearPickerWrap>
            <YearPicker
              value={String(year)}
              onChange={(yr) => { if (yr) setYear(Number(yr)); }}
              placeholder="Select year"
            />
          </YearPickerWrap>
          <FYBadge>April {year} — March {year + 1}</FYBadge>
        </YearPickerRow>

        {/* Stats */}
        <StatsRow>
          <StatCard $bg="#f0fdf4" $border="#bbf7d0">
            <StatLabel>Annual Total (FY {year}/{String(year + 1).slice(-2)})</StatLabel>
            <StatValue $color="#15803d" style={{ fontSize: '20px' }}>
              {grandTotal > 0
                ? `LKR ${grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : '—'}
            </StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Months Set</StatLabel>
            <StatValue>{filledCount} / 12</StatValue>
          </StatCard>
          {isDirty && (
            <StatCard $bg="#fffbeb" $border="#fcd34d">
              <StatLabel>Unsaved Changes</StatLabel>
              <StatValue $color="#b45309">{dirtyKeys.length} month{dirtyKeys.length !== 1 ? 's' : ''}</StatValue>
            </StatCard>
          )}
        </StatsRow>

        {/* Table */}
        <TableCard>
          <CardHeader>
            <div>
              <CardTitle>Monthly Branch Target — FY {year}/{String(year + 1).slice(-2)}</CardTitle>
              <CardSubtitle>
                Enter the target amount for each month. Changes are highlighted until saved.
              </CardSubtitle>
            </div>
          </CardHeader>

          {error && <ErrorBanner>{error}</ErrorBanner>}

          <TableWrapper>
            <StyledTable>
              <thead>
                <tr>
                  <Th style={{ width: '40px' }}>#</Th>
                  <Th>Month</Th>
                  <Th $right>Target Amount (LKR)</Th>
                  <Th style={{ width: '160px', textAlign: 'center' }}>KPI</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <LoadingRow>
                    <td colSpan={3}>Loading…</td>
                  </LoadingRow>
                ) : (
                  FY_MONTHS.map(({ label, calMonth, offset }, idx) => {
                    const calYear = year + offset;
                    const k = mKey(calYear, calMonth);
                    const dirty = values[k] !== (original[k] ?? '');
                    const quarter = getQuarterForIndex(idx);

                    // Quarter separator row
                    const quarterRow = quarter ? (
                      <QuarterTr key={`q-${quarter.label}`}>
                        <Td colSpan={3} style={{ paddingLeft: '16px' }}>
                          {quarter.label} ({FY_MONTHS[quarter.indices[0]].label} – {FY_MONTHS[quarter.indices[2]].label})
                        </Td>
                        <Td style={{ textAlign: 'right' }}>
                          <QuarterTotal>
                            {(() => {
                              const total = quarter.indices.reduce((s, i) => {
                                const m = FY_MONTHS[i];
                                return s + parseVal(values[mKey(year + m.offset, m.calMonth)]);
                              }, 0);
                              return total > 0
                                ? total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                : '—';
                            })()}
                          </QuarterTotal>
                        </Td>
                      </QuarterTr>
                    ) : null;

                    return (
                      <React.Fragment key={k}>
                        {quarterRow}
                        <Tr>
                          <Td style={{ color: '#94a3b8', fontSize: '12px' }}>{idx + 1}</Td>
                          <Td>
                            <MonthChip>
                              {label}
                              <YearChip>{calYear}</YearChip>
                              {kpiCounts[k] > 0 && (
                                <KpiCountBadge $set>
                                  {kpiCounts[k]} KPI{kpiCounts[k] !== 1 ? 's' : ''}
                                </KpiCountBadge>
                              )}
                            </MonthChip>
                          </Td>
                          <Td style={{ textAlign: 'right' }}>
                            <CellInput
                              type="text"
                              inputMode="decimal"
                              $dirty={dirty}
                              value={values[k] ?? ''}
                              placeholder="0.00"
                              onChange={(e) => handleChange(k, e.target.value)}
                              onBlur={() => handleBlur(k)}
                              aria-label={`Target for ${label} ${calYear}`}
                            />
                          </Td>
                          <Td style={{ textAlign: 'center' }}>
                            <KpiBtn
                              type="button"
                              disabled={!targetIds[k]}
                              title={!targetIds[k] ? 'Save target first to set KPI' : `Set KPI for ${label} ${calYear}`}
                              onClick={() => openKpiModal(k, `${label} ${calYear}`)}
                            >
                              Set KPI
                            </KpiBtn>
                          </Td>
                        </Tr>
                      </React.Fragment>
                    );
                  })
                )}
                {/* Grand total row */}
                {!loading && (
                  <tr style={{ borderTop: '2px solid #e2e8f0' }}>
                    <Td colSpan={3} style={{ fontWeight: 700, fontSize: '14px', background: '#f8fafc' }}>
                      Total
                    </Td>
                    <Td style={{ textAlign: 'right', background: '#f8fafc' }}>
                      <span style={{ fontWeight: 800, fontSize: '16px', color: '#15803d' }}>
                        {grandTotal > 0
                          ? grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '—'}
                      </span>
                    </Td>
                    <Td style={{ background: '#f8fafc' }} />
                  </tr>
                )}
              </tbody>
            </StyledTable>
          </TableWrapper>
        </TableCard>

        {/* Sticky save bar */}
        <SaveBar $visible={isDirty}>
          <SaveBarLeft>
            <SaveBarTitle>Unsaved Changes</SaveBarTitle>
            <SaveBarSub>{dirtyKeys.length} month{dirtyKeys.length !== 1 ? 's' : ''} modified</SaveBarSub>
          </SaveBarLeft>
          <SaveBarActions>
            <DiscardBtn type="button" onClick={handleDiscard} disabled={saving}>
              Discard
            </DiscardBtn>
            <SaveBtn type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  Save Changes
                </>
              )}
            </SaveBtn>
          </SaveBarActions>
        </SaveBar>

      </PageSection>

      {/* KPI Modal */}
      {kpiModal && (
        <ModalBackdrop onClick={(e) => { if (e.target === e.currentTarget) closeKpiModal(); }}>
          <ModalBox>
            <ModalHeader>
              <div>
                <ModalTitle>Set Counselor KPI — {kpiModal.monthLabel}</ModalTitle>
                <ModalSubtitle>Assign KPI points and optional amount targets to each counselor.</ModalSubtitle>
              </div>
              <ModalClose type="button" onClick={closeKpiModal} aria-label="Close">×</ModalClose>
            </ModalHeader>

            <ModalBody>
              {kpiModalError && <ModalError>{kpiModalError}</ModalError>}
              {kpiModalLoading ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: '14px' }}>Loading…</div>
              ) : (
                <>
                  {kpiRows.length > 0 && (
                    <KpiRowHeader>
                      <KpiHeaderCell>Counselor</KpiHeaderCell>
                      <KpiHeaderCell style={{ textAlign: 'right' }}>KPI Point</KpiHeaderCell>
                      <KpiHeaderCell style={{ textAlign: 'right' }}>KPI Amount (opt.)</KpiHeaderCell>
                      <span />
                    </KpiRowHeader>
                  )}
                  {kpiRows.map((row) => (
                    <KpiRow key={row._uid}>
                      <KpiSelect>
                        <Select
                          isSearchable
                          placeholder="— Select counselor —"
                          isLoading={staffLoading}
                          options={staffOptions.map((s) => ({ value: String(s.staffId), label: s.name }))}
                          value={row.staff_id ? { value: row.staff_id, label: staffOptions.find((s) => String(s.staffId) === row.staff_id)?.name || row.staff_id } : null}
                          onChange={(opt) => updateKpiRow(row._uid, 'staff_id', opt ? opt.value : '')}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              minHeight: '36px',
                              fontSize: '13px',
                              borderColor: state.isFocused ? '#10b981' : '#e2e8f0',
                              boxShadow: state.isFocused ? '0 0 0 2px rgba(16,185,129,0.2)' : 'none',
                              background: '#f8fafc',
                              '&:hover': { borderColor: '#10b981' },
                            }),
                            option: (base, state) => ({
                              ...base,
                              fontSize: '13px',
                              background: state.isSelected ? '#10b981' : state.isFocused ? '#f0fdf4' : '#fff',
                              color: state.isSelected ? '#fff' : '#0f172a',
                            }),
                            menu: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                      </KpiSelect>
                      <KpiInput
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={row.kpi_point}
                        onChange={(e) => updateKpiRow(row._uid, 'kpi_point', e.target.value)}
                      />
                      <KpiInput
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="optional"
                        value={row.kpi_amount}
                        onChange={(e) => updateKpiRow(row._uid, 'kpi_amount', e.target.value)}
                      />
                      <RemoveBtn type="button" onClick={() => removeKpiRow(row._uid)} title="Remove row">×</RemoveBtn>
                    </KpiRow>
                  ))}
                  <AddRowBtn type="button" onClick={addKpiRow}>+ Add Counselor</AddRowBtn>
                </>
              )}
            </ModalBody>

            <ModalFooter>
              <ModalCancelBtn type="button" onClick={closeKpiModal} disabled={kpiModalSaving}>Cancel</ModalCancelBtn>
              <ModalSaveBtn type="button" onClick={handleKpiSave} disabled={kpiModalSaving || kpiModalLoading}>
                {kpiModalSaving ? 'Saving…' : 'Save KPI'}
              </ModalSaveBtn>
            </ModalFooter>
          </ModalBox>
        </ModalBackdrop>
      )}
    </AdminSalesLayout>
  );
}
